<template>
  <Modal
    ref="modal"
    class="GroupAssignDialog"
    :heading="messages.group.addMember"
    :size="
      isSmallPixel ? (isExtraSmallPixel ? 'extraSmall' : 'small') : 'medium'
    "
    @closeModal="onClickModalClose"
  >
    <div class="GroupAssignDialog__layout">
      <div class="GroupAssignDialog__inner">
        <SearchForm
          id="search"
          class="GroupAssignDialog__searchForm"
          name="search"
          :placeholder="messages.common.searchPlaceholder"
          :value="searchWord"
          @inputWord="searchWord = $event"
        />
      </div>
      <div class="GroupAssignDialog__info">
        <p>
          {{ messages.group.groupManagement.info.groupAssign1 }}
        </p>
        <p class="GroupAssignDialog__info">
          {{ messages.group.groupManagement.info.groupAssign2 }}
        </p>
      </div>
      <div class="GroupAssignDialog__userListWrapper">
        <UserList
          class="GroupAssignDialog__userList"
          :displayUsers="displayUsersValue"
          :noDataText="noDataText"
          :noDataImgUrl="noDataImageUrl"
          :isListLoading="isListLoading"
          :headList="headList"
          :isThChecked="isThChecked"
          @onCheckHeader="onCheckHeader"
          @onCheckItem="onCheckItem"
        />
      </div>
      <div class="GroupAssignDialog__info">
        {{ countDisplayLabel }}
      </div>
      <div class="GroupAssignDialog__paginationWrapper">
        <PaginationBar
          class="GroupAssignDialog__pagination"
          :currentPage="currentPage"
          :maxDisplayPages="maxDisplayPages"
          :itemsPerPage="MAX_DISPLAY_NUM"
          :total="totalCount"
          @onClick:pageNumber="movePage"
        />
      </div>

      <div class="GroupAssignDialog__modalInner">
        <div class="GroupAssignDialog__modalButtonDiv">
          <Button
            class="GroupAssignDialog__modalButton"
            tag="button"
            dynamics="secondary"
            :label="messages.common.cancel"
            @click="onClickModalClose"
          />
          <Button
            class="GroupAssignDialog__modalButton"
            tag="button"
            dynamics="primary"
            :label="messages.common.add"
            :isDisabled="isButtonDisabled"
            @click="onClickAssign"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts">
export default {
  name: 'GroupAssignDialog',
};
</script>

<script lang="ts" setup>
import Button from '@spectee/components/Atoms/Button/index.vue';
import UserList from '@spectee/components/Organisms/UserList/index.vue';
import Modal from '@spectee/components/Organisms/Modal/index.vue';
import { ref, computed, watch, watchEffect } from 'vue';
import { userStatus } from '@spectee/ts/userStatus';
import SearchForm from '@spectee/components/Atoms/SearchForm/index.vue';
import PaginationBar from '@spectee/components/Molecules/PaginationBar/index.vue';
import { useWindowWidth } from '@spectee/composables/useWindowWidth';
import noDataImage from '@spectee/assets/images/img_notFound.png';
import noSearchDataImage from '@spectee/assets/images/img_search_noItem.svg';
import { HeadItem } from '@spectee/types/atoms';
import messages from '../../../../messages.json';

interface Props {
  displayUsers: any[];
  isListLoading: boolean;
}

const props = withDefaults(defineProps<Props>(), {});

const { isSmallPixel, isExtraSmallPixel } = useWindowWidth();
const MAX_DISPLAY_NUM = 50;
const maxDisplayPages = 5;
const currentPage = ref(1);
const isThChecked = ref(false);

const searchWord = ref('');
const checkedUsersCount = ref(0);

// フィルター後のユーザー一覧
const filteredUser = computed(() => {
  if (searchWord.value) {
    const regex = new RegExp(searchWord.value, 'i');
    return displayUsersRef.value.filter(
      userItem =>
        regex.test(userItem.user.name) ||
        regex.test(userItem.user.email) ||
        regex.test(userItem.user.authority?.displayName)
    );
  }
  return displayUsersRef.value;
});

const totalCount = computed(() => {
  return filteredUser.value.length;
});

const start = computed(() => (currentPage.value - 1) * MAX_DISPLAY_NUM);
const end = computed(() =>
  Math.min(start.value + MAX_DISPLAY_NUM, totalCount.value)
);

const displayUsersValue = computed(() =>
  filteredUser.value.slice(start.value, end.value)
);

const movePage = (pageNumber: number) => {
  currentPage.value = pageNumber;
};

const displayUsersRef = ref(props.displayUsers);

const userIdsToAdd = computed(() => {
  return displayUsersRef.value
    .filter(user => user.isChecked)
    .map(user => user.user.auth0UserId);
});

const isButtonDisabled = computed(() => {
  return checkedUsersCount.value === 0 ? true : false;
});

const headList: HeadItem[] = [
  { key: 'name', label: messages.common.headList.name },
  { key: 'displayName', label: messages.common.headList.displayName },
  { key: 'email', label: messages.common.headList.email },
  { key: 'groupName', label: messages.common.headList.groupName },
  { key: 'authority', label: messages.common.headList.authority },
];

const emit = defineEmits<{
  (e: 'onClickModalClose'): void;
  (e: 'onClickAssign', params: string[]): void;
}>();

const onClickModalClose = () => {
  emit('onClickModalClose');
};

const onClickAssign = () => {
  emit('onClickAssign', userIdsToAdd.value);
};

const onCheckItem = (event: Event, checkedUser: any) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  // Update the checked state of the clicked user
  displayUsersRef.value.forEach(user => {
    if (
      checkedUser.status === user.status &&
      checkedUser.status === userStatus.active &&
      user.user.auth0UserId === checkedUser.user.auth0UserId
    ) {
      user.isChecked = target.checked;
      checkedUsersCount.value = checkedTotalItemsLength();
    }
  });
};

const onCheckHeader = (event: Event) => {
  const { target } = event;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }
  displayUsersRef.value.forEach(user => {
    if (displayUsersValue.value.includes(user)) {
      user.isChecked = target.checked;
    }
  });
  checkedUsersCount.value = checkedTotalItemsLength();
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  isThChecked.value =
    checkedDisplayUsersCount === displayUsersValue.value?.length ? true : false;
};

const checkedDisplayItemsLength = () => {
  return displayUsersValue.value.filter(user => user.isChecked).length;
};

const checkedTotalItemsLength = () => {
  return displayUsersRef.value.filter(user => user.isChecked).length;
};

watchEffect(() => {
  const checkedDisplayUsersCount = checkedDisplayItemsLength();
  isThChecked.value =
    checkedDisplayUsersCount === displayUsersValue.value.length ? true : false;
});

watch(
  () => props.displayUsers,
  async newVal => {
    if (newVal) {
      displayUsersRef.value = newVal;
    }
  }
);

const noDataText = messages.group.groupManagement.info.noMatchingMembers;

const noDataImageUrl = computed(() => {
  if (filteredUser.value.length === 0 && displayUsersRef.value.length !== 0) {
    return noSearchDataImage;
  } else {
    return noDataImage;
  }
});

const countDisplayLabel = computed(() =>
  messages.group.groupManagement.info.checkedUsersCount.replace(
    '{{count}}',
    checkedUsersCount.value.toString()
  )
);
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;
.GroupAssignDialog {
  &__layout {
    display: grid;
    // SearchForm, UserList, Pagination, Buttons
    grid-template-rows:
      auto
      1fr
      auto
      auto;
    gap: $service-unit * 2;
    max-height: 80vh;
  }

  &__userListWrapper {
    overflow: auto;
    min-height: 0;
  }

  &__userList {
    height: 100%;
    display: grid;
    grid-auto-rows: min-content;
  }

  &__modalInner {
    padding-top: $service-unit;
    text-align: center;
  }

  &__modalButtonDiv {
    display: flex;
    justify-content: center;
    gap: 12px;
  }

  &__paginationWrapper {
    display: flex;
    flex-direction: column;
    gap: $service-unit;
  }

  &__pagination {
    display: flex;
    grid-template-columns: none;
    gap: $service-unit * 10;
  }

  &__searchForm {
    margin-top: $service-unit * 3;
    width: 50%;
    max-width: $service-unit * 100;
  }

  &__info {
    font-size: $service-font-size-mp;
    color: $color-gray-60;
  }

  @media (max-width: 425px) {
    &__pagination {
      display: block;
    }

    &__info,
    &__modalButton {
      font-size: $service-font-size-ppp;
    }
  }
}
</style>
